'use client';

import { type PropsWithChildren, useEffect, useRef } from 'react';

const ARROW_SELECTOR = '.sparky-c-horizontal-overflow-carousel__button';
const LISTENER_OPTIONS = { capture: true, passive: true } as const;

function stopArrowTouchStart(event: TouchEvent) {
    if (event.target instanceof Element && event.target.closest(ARROW_SELECTOR)) {
        event.stopPropagation();
    }
}

/**
 * Wraps a Sparky `HorizontalOverflowCarousel` so its arrows react only to `click`.
 *
 * Glide moves the carousel on the arrow's `touchstart` and hides the last arrow right away,
 * so the tap's synthetic `click` lands on the product card under the arrow and opens it.
 */
export function CarouselArrowTouchGuard({ children }: Readonly<PropsWithChildren>) {
    const ref = useRef<HTMLDivElement>(null);

    useEffect(() => {
        const root = ref.current;
        if (!root) return;

        root.addEventListener('touchstart', stopArrowTouchStart, LISTENER_OPTIONS);
        return () => root.removeEventListener('touchstart', stopArrowTouchStart, LISTENER_OPTIONS);
    }, []);

    return (
        <div ref={ref} style={{ display: 'contents' }}>
            {children}
        </div>
    );
}
